import debugFactory from 'debug';
import type debug from 'debug';
import { getRequestContext, type RequestContextData } from '../context/RequestContext';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type DebugInitSource = 'url' | 'storage' | 'env' | 'none';

type DebuggerInstance = ReturnType<typeof debugFactory>;

/** Logger method that accepts any arguments */
type LogMethod = (...args: unknown[]) => void;

export interface Logger {
  debug: LogMethod;
  info: LogMethod;
  warn: LogMethod;
  error: LogMethod;
}

export interface DebugInitResult {
  source: DebugInitSource;
  pattern?: string;
}

const logMethods: Partial<Record<LogLevel, (...args: unknown[]) => void>> = {};

if (typeof console !== 'undefined') {
  logMethods.debug = (console.debug ?? console.log).bind(console);
  logMethods.info = (console.info ?? console.log).bind(console);
  logMethods.warn = (console.warn ?? console.log).bind(console);
  logMethods.error = (console.error ?? console.log).bind(console);
}

const LOG_METHODS: Record<LogLevel, ((...args: unknown[]) => void) | undefined> = {
  debug: logMethods.debug,
  info: logMethods.info,
  warn: logMethods.warn,
  error: logMethods.error,
};

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function applyLogMethod(instance: DebuggerInstance, level: LogLevel): DebuggerInstance {
  const logMethod = LOG_METHODS[level];
  if (logMethod) {
    instance.log = logMethod;
  }
  return instance;
}

function buildDebugger(namespace: string, level: LogLevel): DebuggerInstance {
  return applyLogMethod(debugFactory(`${namespace}:${level}`), level);
}

// ─────────────────────────────────────────────────────────────────────────────
// Request Context Integration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mask email for privacy: "hassan@inovisum.com" → "has...@inovisum.com"
 * @internal Exported for testing only
 */
export function maskEmail(email: string): string {
  const atIndex = email.indexOf('@');
  if (atIndex <= 0) return email;

  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex);

  // Show first 3 chars of local part, or all if shorter
  const visible = Math.min(3, local.length);
  const masked = local.length > visible ? `${local.slice(0, visible)}...` : local;

  return `${masked}${domain}`;
}

/**
 * Format request context as log prefix.
 * Format: [correlationId:8] [maskedEmail] (userId:8)
 * @internal Exported for testing only
 */
export function formatContextPrefix(ctx: RequestContextData): string {
  const parts: string[] = [];

  // Short correlation ID (first 8 chars for readability)
  parts.push(`[${ctx.correlationId.slice(0, 8)}]`);

  // Masked email
  if (ctx.userEmail) {
    parts.push(`[${maskEmail(ctx.userEmail)}]`);
  }

  // User ID in parentheses after email (first 8 chars)
  if (ctx.userId) {
    parts.push(`(${ctx.userId.slice(0, 8)})`);
  }

  return parts.join(' ');
}

/**
 * Wrap a debug instance to automatically prepend request context.
 */
function wrapWithContext(instance: DebuggerInstance): LogMethod {
  return (...args: unknown[]) => {
    // Only add context on server-side (AsyncLocalStorage is Node.js only)
    if (!isBrowser()) {
      const ctx = getRequestContext();
      if (ctx) {
        const prefix = formatContextPrefix(ctx);
        if (typeof args[0] === 'string') {
          args[0] = `${prefix} ${args[0]}`;
        } else {
          args.unshift(prefix);
        }
      }
    }
    instance(...args);
  };
}

// No persistence: debug patterns are applied in-memory only.

function applyDebugPattern(pattern: string): string | null {
  const normalized = pattern
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
    .join(',');

  if (!normalized) {
    debugFactory.disable();
    return null;
  }

  debugFactory.enable(normalized);
  // No persistence: in-memory only.

  return normalized;
}

export function createLogger(namespace: string): Logger {
  return {
    debug: wrapWithContext(buildDebugger(namespace, 'debug')),
    info: wrapWithContext(buildDebugger(namespace, 'info')),
    warn: wrapWithContext(buildDebugger(namespace, 'warn')),
    error: wrapWithContext(buildDebugger(namespace, 'error')),
  };
}

export function enableDebug(pattern: string): void {
  applyDebugPattern(pattern);
}

export function disableDebug(): void {
  debugFactory.disable();
}

export function setupDebugFromUrl(search?: string): string | null {
  if (!isBrowser()) {
    return null;
  }

  try {
    const query = typeof search === 'string' ? search : window.location.search;
    if (!query || query.length === 0) {
      return null;
    }

    const params = new URLSearchParams(query);
    const debugParam = params.get('debug');
    if (!debugParam || debugParam.trim().length === 0) {
      return null;
    }

    // Do not persist URL-provided patterns; they are session-only.
    const normalized = applyDebugPattern(debugParam);
    return normalized ?? debugParam;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[logger] Failed to parse debug parameter from URL', error);
    }
    return null;
  }
}

// Backwards-compat: previously returned localStorage pattern; now always null.
export function loadPersistedDebugPattern(): string | null {
  return null;
}

export function initializeDebug(): DebugInitResult {
  if (!isBrowser()) {
    return { source: 'none' };
  }

  try {
    // Allow explicit URL opt-in for ad‑hoc debugging (non‑persistent).
    const fromUrl = setupDebugFromUrl();
    if (fromUrl) {
      return { source: 'url', pattern: fromUrl };
    }

    // Ensure no residual namespaces remain enabled by default.
    debugFactory.disable();
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[logger] Failed to initialize debug namespaces', error);
    }
  }

  return { source: 'none' };
}
